import React, { useState } from 'react';
import { useToday, useWeek, useBuyHistory, useSellHistory } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PnlValue } from '@/components/common/PnlValue';
import { DataTable, Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { TableSkeleton, MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { formatINR, formatTimestamp } from '@/lib/utils';
import { Trade } from '@/types/trading';
import { DailyPnlPoint } from '@/types/trading';
import {
  Tag,
  TrendingUp,
  Activity,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  Zap,
  Layers,
  FileCheck,
} from 'lucide-react';

export const HistoryView: React.FC = () => {
  const [activeHistoryTab, setActiveHistoryTab] = useState<'WEEKLY_PERF' | 'BUY_ENTRIES' | 'SELL_EXITS'>('WEEKLY_PERF');

  const {
    data: todayData,
    isLoading: isTodayLoading,
    isError: isTodayError,
    error: todayError,
    refetch: refetchToday,
  } = useToday();

  const { data: weekData } = useWeek();
  const { data: buyData } = useBuyHistory();
  const { data: sellData } = useSellHistory();

  const weeklyDays = weekData?.days || [];
  const buyOrders = buyData?.buy_orders || [];
  const sellOrders = sellData?.sell_orders || [];

  // Weekly Days Columns
  const weeklyColumns: Column<DailyPnlPoint>[] = [
    {
      key: 'date',
      header: 'Day / Session',
      sortable: true,
      render: (day) => (
        <span className="font-mono text-slate-200 font-bold text-xs flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-cyan-400" />
          {day.date}
        </span>
      ),
    },
    {
      key: 'trades_count',
      header: 'Trades Count',
      sortable: true,
      align: 'right',
      render: (day) => (
        <span className="font-mono text-slate-300 tabular-nums">{day.trades_count}</span>
      ),
    },
    {
      key: 'pnl',
      header: 'Realized Daily P&L',
      sortable: true,
      align: 'right',
      render: (day) => (
        <PnlValue
          value={day.pnl}
          showSign={true}
          size="sm"
        />
      ),
    },
    {
      key: 'status',
      header: 'Session Outcome',
      align: 'center',
      render: (day) => (
        <StatusBadge
          status={day.pnl > 0 ? 'COMPLETED' : day.pnl < 0 ? 'WARNING' : 'IDLE'}
          label={day.pnl > 0 ? 'PROFITABLE' : day.pnl < 0 ? 'DRAWDOWN' : 'NEUTRAL'}
          size="sm"
          showDot={false}
        />
      ),
    },
  ];

  // Buy/Sell Order Columns
  const tradeColumns: Column<Trade>[] = [
    {
      key: 'symbol',
      header: 'Symbol & Side',
      sortable: true,
      render: (t) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-slate-100 font-mono text-sm">{t.symbol}</span>
            <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
              NSE
            </span>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-mono font-bold uppercase border ${
              t.side === 'BUY'
                ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
                : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
            }`}
          >
            {t.side === 'BUY' ? (
              <ArrowUpRight className="w-2.5 h-2.5 text-emerald-400" />
            ) : (
              <ArrowDownRight className="w-2.5 h-2.5 text-rose-400" />
            )}
            {t.side}
          </span>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'right',
      render: (t) => <span className="font-mono text-slate-200 tabular-nums">{t.quantity}</span>,
    },
    {
      key: 'position_value',
      header: 'Position Value',
      align: 'right',
      render: (t) => (
        <span className="font-mono text-slate-200 tabular-nums">
          {formatINR(t.quantity * t.price)}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (t) => <span className="font-mono text-slate-200 tabular-nums">{formatINR(t.price)}</span>,
    },
    {
      key: 'pnl',
      header: 'Realized P&L',
      align: 'right',
      render: (t) => (
        <PnlValue
          value={t.pnl}
          percent={t.pnl_percent}
          showSign={true}
          showPercent={true}
          layout="stacked"
          align="right"
          nullPlaceholder="—"
        />
      ),
    },
    {
      key: 'reason',
      header: 'Reason / Strategy Source',
      render: (t) => (
        <span className="text-xs text-slate-300 font-mono max-w-xs truncate block" title={t.reason || '—'}>
          {t.reason || '—'}
        </span>
      ),
    },
    {
      key: 'timestamp',
      header: 'Execution Time',
      sortable: true,
      render: (t) => (
        <span className="font-mono text-slate-400 text-xs">
          {formatTimestamp(t.timestamp, 'compact')}
        </span>
      ),
    },
  ];

  if (isTodayLoading && !todayData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Buy / Sell History & Performance"
          icon={Tag}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <TableSkeleton rows={5} cols={6} />
      </div>
    );
  }

  if (isTodayError && !todayData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Buy / Sell History & Performance"
          icon={Tag}
        />
        <ErrorState
          title="Failed to load performance history"
          message={todayError instanceof Error ? todayError.message : 'API connection error'}
          onRetry={() => refetchToday()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Buy / Sell History & Attribution"
          description="Audit performance across rolling sessions with clear attribution between Telegram signals, orders, fills, and position square-offs."
          icon={Tag}
          badge={
            <div className="flex items-center gap-2">
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
              <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-900 text-slate-300 border border-slate-800">
                WEEKLY P&L: {formatINR(weekData?.weekly_pnl, { showSign: true })}
              </span>
            </div>
          }
        />
      </div>

      {/* Lifecycle Architecture Banner */}
      <div className="p-4 rounded-lg border border-slate-800 bg-slate-950/60 space-y-3 animate-stagger-2">
        <div className="text-xs font-mono font-bold text-slate-300 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
          <span>EXECUTION LIFECYCLE SEPARATION</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs font-mono">
          <div className="p-2.5 rounded bg-slate-900/80 border border-slate-800 space-y-1 interactive-card">
            <span className="text-[10px] text-cyan-400 font-bold flex items-center gap-1">
              <Zap className="w-3 h-3" /> 1. TELEGRAM SIGNAL
            </span>
            <p className="text-[11px] text-slate-400">Raw setup message parsed from channel.</p>
          </div>
          <div className="p-2.5 rounded bg-slate-900/80 border border-slate-800 space-y-1 interactive-card">
            <span className="text-[10px] text-amber-400 font-bold flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> 2. RISK VALIDATION
            </span>
            <p className="text-[11px] text-slate-400">Checks loss limit & capacity caps.</p>
          </div>
          <div className="p-2.5 rounded bg-slate-900/80 border border-slate-800 space-y-1 interactive-card">
            <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
              <FileCheck className="w-3 h-3" /> 3. BROKER ORDER
            </span>
            <p className="text-[11px] text-slate-400">Simulated fill execution at LTP.</p>
          </div>
          <div className="p-2.5 rounded bg-slate-900/80 border border-slate-800 space-y-1 interactive-card">
            <span className="text-[10px] text-purple-400 font-bold flex items-center gap-1">
              <Layers className="w-3 h-3" /> 4. ACTIVE POSITION
            </span>
            <p className="text-[11px] text-slate-400">Tracked with stop-loss & target.</p>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-3">
        <MetricCard
          title="Weekly Realized P&L"
          value={
            <PnlValue
              value={weekData?.weekly_pnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle={`Across ${weekData?.trades_count || 0} trades this week`}
          variant={weekData?.weekly_pnl && weekData.weekly_pnl > 0 ? 'emerald' : 'default'}
          icon={TrendingUp}
        />

        <MetricCard
          title="Weekly Win Rate"
          value={
            <span className="text-emerald-400">
              {weekData?.win_rate !== undefined ? `${weekData.win_rate.toFixed(1)}%` : '—'}
            </span>
          }
          subtitle="Profitable trade proportion"
          variant="cyan"
          icon={Activity}
        />

        <MetricCard
          title="Today P&L"
          value={
            <PnlValue
              value={todayData?.today_pnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle={`${todayData?.trades_count || 0} trades today`}
          variant={todayData?.today_pnl && todayData.today_pnl > 0 ? 'emerald' : 'default'}
          icon={TrendingUp}
        />

        <MetricCard
          title="Executed Orders Count"
          value={`${buyOrders.length} BUY / ${sellOrders.length} SELL`}
          subtitle="Audit volume in current period"
          variant="default"
          icon={Tag}
        />
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="space-y-4 animate-stagger-4">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveHistoryTab('WEEKLY_PERF')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 whitespace-nowrap ${
              activeHistoryTab === 'WEEKLY_PERF'
                ? 'bg-slate-800 text-slate-100 border border-slate-700 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            Daily Session Performance (Mon–Fri)
          </button>
          <button
            onClick={() => setActiveHistoryTab('BUY_ENTRIES')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 whitespace-nowrap flex items-center gap-1.5 ${
              activeHistoryTab === 'BUY_ENTRIES'
                ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
            BUY Entry History ({buyOrders.length})
          </button>
          <button
            onClick={() => setActiveHistoryTab('SELL_EXITS')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 whitespace-nowrap flex items-center gap-1.5 ${
              activeHistoryTab === 'SELL_EXITS'
                ? 'bg-rose-950/80 text-rose-300 border border-rose-800/80 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" />
            SELL Exit History ({sellOrders.length})
          </button>
        </div>

        {/* Content Rendering based on Tab */}
        {activeHistoryTab === 'WEEKLY_PERF' && (
          <DataTable
            data={weeklyDays}
            columns={weeklyColumns}
            keyExtractor={(day) => day.date}
            pageSize={7}
            emptyTitle="No weekly history data"
            emptyDescription="Daily session breakdowns will appear here."
          />
        )}

        {activeHistoryTab === 'BUY_ENTRIES' && (
          <DataTable
            data={buyOrders}
            columns={tradeColumns}
            keyExtractor={(ord) => ord.id}
            pageSize={10}
            emptyTitle="No BUY orders recorded"
            emptyDescription="BUY trade entries will appear here once executed."
          />
        )}

        {activeHistoryTab === 'SELL_EXITS' && (
          <DataTable
            data={sellOrders}
            columns={tradeColumns}
            keyExtractor={(ord) => ord.id}
            pageSize={10}
            emptyTitle="No SELL exits recorded"
            emptyDescription="SELL exits and profit bookings will appear here."
          />
        )}
      </div>
    </div>
  );
};
